import React from "react";
import { useNavigate } from "react-router-dom";
import { MdAdd, MdRefresh, MdSearch, MdLayers, MdEdit, MdDelete, MdToggleOn, MdToggleOff } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import Card from "components/card";
import { Skeleton } from "components/ui/Skeleton";
import EmptyState from "components/ui/EmptyState";
import ConfirmDialog from "components/ui/ConfirmDialog";
import { instructorBundlesService, type InstructorBundle } from "services/instructorBundlesService";

function useDebounced<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function money(value: number | null | undefined, currency?: string | null, isArabic?: boolean) {
  const amount = Number(value ?? 0);
  const cur = currency || "EGP";
  return isArabic ? `${amount} ${cur}` : `${amount} ${cur}`;
}

function BundleCard({
  bundle,
  isArabic,
  onEdit,
  onToggle,
  onDelete,
}: {
  bundle: InstructorBundle;
  isArabic: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const imageSrc = bundle.image_url ?? null;
  const discount = Number(bundle.discount_amount ?? 0);
  const discountPct = Number(bundle.discount_percentage ?? 0);

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md dark:border-white/10 dark:bg-navy-800">
      <div className="relative h-32 w-full overflow-hidden bg-gradient-to-br from-indigo-400 to-purple-600">
        {imageSrc ? (
          <img src={imageSrc} alt={bundle.title} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <MdLayers className="h-10 w-10 text-white/90" />
          </div>
        )}
        <span
          className={`absolute left-3 top-3 rounded-full px-2 py-0.5 text-[11px] font-bold shadow ${
            bundle.is_active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
          }`}
        >
          {bundle.is_active ? (isArabic ? "مفعّل" : "Active") : isArabic ? "غير مفعّل" : "Inactive"}
        </span>
        {discountPct > 0 && (
          <span className="absolute right-3 top-3 rounded-full bg-rose-500 px-2 py-0.5 text-[11px] font-bold text-white shadow">
            -{discountPct}%
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-1 text-base font-bold text-gray-800 dark:text-white" title={bundle.title}>
          {bundle.title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-300">
          {isArabic ? "كورسات العرض" : "Bundle courses"}: {bundle.courses_count ?? 0}
        </p>
        <div className="mt-1 space-y-1 text-sm">
          <div className="flex items-center justify-between text-gray-500">
            <span>{isArabic ? "السعر الأصلي" : "Original price"}</span>
            <span className="line-through">{money(bundle.original_total_price, bundle.currency, isArabic)}</span>
          </div>
          <div className="flex items-center justify-between font-bold text-navy-700 dark:text-white">
            <span>{isArabic ? "سعر العرض" : "Bundle price"}</span>
            <span>{money(bundle.price, bundle.currency, isArabic)}</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between text-xs font-semibold text-green-600">
              <span>{isArabic ? "التوفير" : "You save"}</span>
              <span>{money(discount, bundle.currency, isArabic)}</span>
            </div>
          )}
        </div>
        <div className="mt-auto flex items-center justify-end gap-1 border-t border-gray-100 pt-3 dark:border-white/10">
          <button
            type="button"
            onClick={onEdit}
            title={isArabic ? "تعديل" : "Edit"}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
          >
            <MdEdit className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onToggle}
            title={bundle.is_active ? (isArabic ? "تعطيل" : "Deactivate") : isArabic ? "تفعيل" : "Activate"}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
          >
            {bundle.is_active ? <MdToggleOn className="h-5 w-5 text-green-500" /> : <MdToggleOff className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={onDelete}
            title={isArabic ? "حذف" : "Delete"}
            className="rounded-lg p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"
          >
            <MdDelete className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BundlesListPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();

  const [items, setItems] = React.useState<InstructorBundle[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [term, setTerm] = React.useState("");
  const [deleteTarget, setDeleteTarget] = React.useState<InstructorBundle | null>(null);
  const debouncedTerm = useDebounced(term, 300);

  const fetchList = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await instructorBundlesService.list({ limit: 100, term: debouncedTerm.trim() || undefined });
      setItems(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, [debouncedTerm]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleToggle = async (bundle: InstructorBundle) => {
    const next = !bundle.is_active;
    setItems((p) => p.map((b) => (b.id === bundle.id ? { ...b, is_active: next } : b)));
    try {
      await instructorBundlesService.setStatus(bundle.id, next);
    } catch {
      setItems((p) => p.map((b) => (b.id === bundle.id ? { ...b, is_active: !next } : b)));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const bundle = deleteTarget;
    const prev = items;
    setItems((p) => p.filter((b) => b.id !== bundle.id));
    try {
      await instructorBundlesService.remove(bundle.id);
    } catch {
      setItems(prev);
      throw new Error("delete_failed");
    }
  };

  return (
    <div className="w-full space-y-5 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "عروض الكورسات" : "Course Bundles"}
          </h1>
          <p className="text-sm text-gray-500">
            {isArabic
              ? "أنشئ عروضًا تجمع أكثر من كورس بسعر مخفّض."
              : "Create offers that bundle multiple courses at a discount."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MdSearch
              className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 ${isArabic ? "right-3" : "left-3"}`}
            />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={isArabic ? "ابحث في العروض..." : "Search bundles..."}
              className={`w-64 rounded-xl border border-gray-200 bg-white py-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white ${
                isArabic ? "pl-3 pr-9" : "pl-9 pr-3"
              }`}
            />
          </div>
          <button
            type="button"
            onClick={() => fetchList()}
            className="inline-flex items-center gap-1 rounded-xl bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-white"
          >
            <MdRefresh className="h-4 w-4" />
            {isArabic ? "تحديث" : "Refresh"}
          </button>
          <button
            type="button"
            onClick={() => navigate("new")}
            className="inline-flex items-center gap-1 rounded-xl bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            <MdAdd className="h-4 w-4" />
            {isArabic ? "إنشاء عرض مجمع" : "Create Bundle"}
          </button>
        </div>
      </header>

      <Card extra="w-full p-4 md:p-6">
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MdLayers className="h-6 w-6" />}
            title={isArabic ? "لا توجد عروض" : "No bundles yet"}
            description={
              isArabic
                ? "أنشئ أول عرض يجمع كورسين أو أكثر بسعر مخفّض."
                : "Create your first bundle combining two or more courses."
            }
            action={
              <button
                type="button"
                onClick={() => navigate("new")}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                <MdAdd className="h-4 w-4" />
                {isArabic ? "إنشاء عرض مجمع" : "Create Bundle"}
              </button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((b) => (
              <BundleCard
                key={b.id}
                bundle={b}
                isArabic={isArabic}
                onEdit={() => navigate(`${b.id}/edit`)}
                onToggle={() => handleToggle(b)}
                onDelete={() => setDeleteTarget(b)}
              />
            ))}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        variant="danger"
        title={isArabic ? "حذف العرض" : "Delete bundle"}
        description={
          isArabic
            ? `هل تريد حذف عرض «${deleteTarget?.title ?? ""}»؟`
            : `Delete bundle “${deleteTarget?.title ?? ""}”?`
        }
        confirmLabel={isArabic ? "حذف" : "Delete"}
      />
    </div>
  );
}
